import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LeafIcon } from 'lucide-react';
import { ShopFaq } from '../shared/ShopFaq';
import { ImageSlot } from '../shared/ImageSlot';
import { GiftCardBlock } from '../shared/GiftCardBlock';
import { HeadingText } from '../shared/HeadingText';
import { RoutineCard } from '../shared/RoutineCard';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { TrustBadges } from '../shared/TrustBadges';
import { Drawer } from '../shared/Drawer';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { pageActions } from '../../data/pageActions';
import { usePages } from '../../contexts/SiteContext';
import { founder } from '../../data/content';
import { listNl, plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { btnPrimary, btnPrimaryLg, btnSecondary, btnSecondaryLg } from '../../ui/styles';
import { heroTitle, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { FilterPanel } from './ShopFilters';
import { COLLECTION_ID, ShopCollection } from './ShopCollection';
import { IngredientIndex } from './IngredientIndex';
import { ROUTINES_SHOWN } from './shopRules';
import { placeholderImages } from '../../data/placeholderImages';
import { useShopFilters } from './useShopFilters';

/**
 * De webshop (docs/VORMGEVING.md › Pagina's). Vraag: wat past bij mijn huid, zonder te gokken?
 * Hoofdknop "Kies op je huidvraag" (naar de collectie met huidfilters), het huidadvies als tweede.
 * kop + collectie (sand) · groot beeld (product in gebruik) · beloftes · protocollen (sand) ·
 * wat er écht in zit (shell) · abonnement (klei, het ene moment) · merken (shell) · vragen (sand) · cadeaubon (blush).
 */
export function Shop() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    products,
    brands,
    routines
  } = useCatalog();
  const f = useShopFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const copy = pages.shop;
  const vars = {
    count: plural(products.length, 'product', 'producten'),
    brands: listNl(brands.map(b => b.name))
  };
  return <>
      {/* Compacte kop: wie hier komt, wil producten zien. Het verhaal staat verderop. */}
      <section className="bg-sand" data-id="element-6808" data-mp="3bveageah7cnd">
        <div className="mx-auto flex max-w-content flex-col gap-5 px-6 pb-2 pt-10 lg:flex-row lg:items-end lg:justify-between lg:gap-12 lg:px-10 lg:pt-16" data-id="element-6809" data-mp="3byabwclj8jgk">
          <div className="max-w-3xl" data-id="element-6810" data-mp="325bhuf79o0pk">
            <h1 className={heroTitle} data-id="element-6811" data-mp="3249wnhwjdxu4">
              <HeadingText heading={copy.hero.heading} data-id="element-6812" data-mp="2y49izzh9hqo4" />
            </h1>
            {copy.hero.body && <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-6813" data-mp="2t680bfnqztd6">{fill(copy.hero.body, vars)}</p>}
          </div>
          {/* Hoofdknop: kiezen op je huid (de collectie met de huidfilters). Twijfel? Het huidadvies, persoonlijk met haar gezicht. */}
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row lg:self-end" data-id="element-6814" data-mp="2j9leoonlkya3">
            <a href={`#${COLLECTION_ID}`} className={btnPrimaryLg} data-id="element-6815" data-mp="293btcjb8r2zv">
              {pageActions.chooseByConcern}
            </a>
            {copy.hero.secondaryCta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`${btnSecondaryLg} group pl-2 text-left`} data-id="element-6816" data-mp="2j2swou30t4so">
                <img src={founder.portrait} alt="" width={36} height={36} className="h-9 w-9 shrink-0 rounded-full object-cover object-top" data-id="element-6817" data-mp="2o02t4totugq0" />
                <span className="leading-snug" data-id="element-6818" data-mp="1vbx783m1elol">{fill(copy.hero.secondaryCta)}</span>
                <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6819" data-mp="20b9x6x87g8ve" />
              </button>}
          </div>
        </div>
        {/* Vertrouwen, geen tweede knop: de salon als bron van de keuze. */}
        {copy.proof && <p className="mx-auto max-w-content px-6 pt-4 text-[14px] text-muted lg:px-10" data-id="element-6820" data-mp="1ckw20xubptwl">{fill(copy.proof)}</p>}
      </section>

      <ShopCollection f={f} onOpenFilters={() => setFiltersOpen(true)} data-id="element-6821" data-mp="3ebedpir7h3hx" />

      {/* Het grote beeld van de webshop: een product in gebruik (docs/BEELDEN.md, W1). Tussen collectie en beloftes. */}
      <div className="bg-sand" data-id="element-6822" data-mp="3o1g26bnh0nxd">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6823" data-mp="3o2heskk4xih7">
          <ImageSlot code="W1" media={{
          image: placeholderImages.shopInUse,
          alt: 'Een druppel serum op een vingertop, boven een open flesje'
        }} className="aspect-[4/3] w-full sm:aspect-[21/9]" sizes="(min-width: 1280px) 1200px, 100vw" data-id="element-6824" data-mp="3j965mcruopm4" />
        </div>
      </div>

      {/* Zekerheden ná de collectie: ze bevestigen de keuze, maar staan het kiezen niet in de weg. */}
      <TrustBadges data-id="element-6825" data-mp="5wcqg8301dnz" />

      {/* Protocollen direct na de collectie: wie twijfelt tussen losse producten, krijgt hier het complete plan. */}
      {routines.length > 0 && <Section tone="sand" labelledBy="shop-routines" contained={false} data-id="element-6826" data-mp="27n7s3gjw072m">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6827" data-mp="n4nhrx1m23tn">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-6828" data-mp="n55kncis3gaj">
              <div className="max-w-2xl" data-id="element-6829" data-mp="s25ix19dzer3">
                <h2 id="shop-routines" className={`${sectionHeading} text-ink`} data-id="element-6830" data-mp="s5bacn5hmspz">
                  <HeadingText heading={copy.routines.heading} data-id="element-6831" data-mp="dchs4tytzyhm" />
                </h2>
                {copy.routines.body && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-6832" data-mp="ibaubq8o432h">{fill(copy.routines.body)}</p>}
              </div>
              {copy.routines.cta && <Link to="/routines" className={`${quietLink} text-ink`} data-id="element-6833" data-mp="9dqqqanc1uop">
                  {fill(copy.routines.cta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6834" data-mp="3qmx7ydlyzc2e" />
                </Link>}
            </div>

            <ul className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-6835" data-mp="1ot7svxkhjy3u">
              {routines.slice(0, ROUTINES_SHOWN).map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-6836" data-mp="1trj1k4z1o2kh">
                  <RoutineCard routine={r} data-id="element-6837" data-mp="1sc6ldj3nvlz9" />
                </li>)}
            </ul>
          </div>
        </Section>}

      {/* Wat er écht in zit: voor wie wil weten wáárom iets werkt. */}
      <IngredientIndex data-id="element-6838" data-mp="1icph96425g5e" />

      <SubscriptionBlock data-id="element-6839" data-mp="1idzfpwetoqck" />

      {/* Merken */}
      <Section tone="shell" labelledBy="shop-brands" contained={false} data-id="element-6840" data-mp="1ec5hdus3nyfk">
        <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-6841" data-mp="1edytvqpe9cvh">
          <div className="lg:col-span-5" data-id="element-6842" data-mp="2b496p7r8zqfu">
            <h2 id="shop-brands" className={`${sectionHeading} text-ink`} data-id="element-6843" data-mp="262s334hdppvk">
              {fill(copy.brands.heading)}
            </h2>
            <p className="mt-6 flex items-start gap-3 text-[17px] leading-relaxed text-muted" data-id="element-6844" data-mp="21as71n4fjh49">
              <LeafIcon className="mt-1.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6845" data-mp="1xa8cmrb8qrlt" />
              {/* De boomclaim alleen zolang emerginC echt in het assortiment zit. */}
              {fill(copy.brands.body)}
              {brands.some(b => b.name === 'emerginC') && ' emerginC plant bovendien een boom voor elk verkocht product.'}
            </p>
          </div>
          <ul className="grid gap-10 sm:grid-cols-2 lg:col-span-7" data-id="element-6846" data-mp="gj5wcuqvaknw">
            {brands.map(b => <li key={b.name} className="border-t border-ink/15 pt-6" data-id="element-6847" data-mp="kieoxwa68chv">
                <h3 className="type-title text-ink" data-id="element-6848" data-mp="pjb2l1kbrd0h">{b.name}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-6849" data-mp="ui5km1agwxjj">{b.description}</p>
              </li>)}
          </ul>
        </div>
      </Section>

      <ShopFaq data-id="element-6850" data-mp="zfe4q7f6fbnf" />

      <GiftCardBlock data-id="element-6851" data-mp="zevz4k6mjl2u" />

      <Drawer open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters" footer={
    // Wissen zo smal als zijn woord, de hoofdknop krijgt de rest: zo past "Toon 43 producten"
    // ook op een telefoon van 320px, zonder afkappen of horizontaal scrollen.
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3" data-id="element-6853" data-mp="1i6x7cd5agnox">
            <button type="button" onClick={f.clear} disabled={!f.hasFilters} className={`${btnSecondary} px-4`} data-id="element-6854" data-mp="t4kqrmwo1t1s">
              Wissen
            </button>
            {/* Bij nul resultaten niet "Toon 0 producten": dan is er niets te tonen. */}
            <button type="button" onClick={() => setFiltersOpen(false)} disabled={f.visible.length === 0} className={`${btnPrimary} min-w-0 px-4`} data-id="element-6855" data-mp="123y4irrtp3sb">
              {f.visible.length === 0 ? 'Geen producten' : `Toon ${plural(f.visible.length, 'product', 'producten')}`}
            </button>
          </div>} data-id="element-6852" data-mp="1487a8xigyo0k">
        <FilterPanel skin={f.skin} setSkin={f.setSkin} step={f.step} setStep={f.setStep} brand={f.brand} setBrand={f.setBrand} showSkin={false} data-id="element-6856" data-mp="5gtgspylls3o" />
      </Drawer>
    </>;
}